import {
  Alert,
  Button,
  Card,
  CardContent,
  MenuItem,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import BoltIcon from '@mui/icons-material/Bolt'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { useState } from 'react'
import { useEbReadings, useHalls } from '@/api/hooks'
import type { EbReading } from '@/api/types'
import { useAuthStore } from '@/auth/authStore'
import { ClosingDialog, CorrectionDialog, OpeningDialog } from '@/pages/utility/EbReadingDialogs'
import {
  EmptyState,
  ErrorBanner,
  formatDateTime,
  formatMoney,
  formatNumber,
  LoadingBlock,
  PageHeader,
  StatusChip,
} from '@/components/common'

export function EbReadingsPage() {
  const canWrite = useAuthStore((state) => state.canWrite)
  const hasRole = useAuthStore((state) => state.hasRole)
  const isAdmin = hasRole('SUPER_ADMIN') || hasRole('MANAGEMENT')
  const [status, setStatus] = useState('')
  const [hallId, setHallId] = useState('')
  const [openingOpen, setOpeningOpen] = useState(false)
  const [closing, setClosing] = useState<EbReading | null>(null)
  const [correcting, setCorrecting] = useState<EbReading | null>(null)
  const can = useAuthStore((state) => state.can)

  const { data: halls } = useHalls()
  const { data, isLoading, error } = useEbReadings({
    status: status || undefined,
    hallId: hallId || undefined,
  })

  const readings = data?.content ?? []
  const stillOpen = readings.filter((reading) => reading.status === 'OPEN')

  return (
    <>
      <PageHeader
        title="EB meter readings"
        subtitle="Opening and closing entries per banquet booking, priced at the hall tariff"
        actions={
          <Stack direction="row" spacing={1}>
            <TextField
              select
              size="small"
              label="Hall"
              value={hallId}
              onChange={(event) => setHallId(event.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All halls</MenuItem>
              {(halls ?? []).map((hall) => (
                <MenuItem key={hall.id} value={hall.id}>
                  {hall.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Status"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">All</MenuItem>
              {['OPEN', 'CLOSED', 'BILLED', 'WAIVED'].map((value) => (
                <MenuItem key={value} value={value}>
                  {value.toLowerCase()}
                </MenuItem>
              ))}
            </TextField>
            {canWrite('utility') ? (
              <Button variant="contained" startIcon={<BoltIcon />} onClick={() => setOpeningOpen(true)}>
                Opening entry
              </Button>
            ) : null}
          </Stack>
        }
      />

      <ErrorBanner error={error} />

      {stillOpen.length > 0 ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {stillOpen.length} meter{stillOpen.length === 1 ? '' : 's'} awaiting a closing entry. The next
          booking on the same meter cannot be opened until these are closed.
        </Alert>
      ) : null}

      <Card>
        <CardContent>
          {isLoading ? <LoadingBlock /> : null}
          {!isLoading && readings.length === 0 ? (
            <EmptyState
              message="No readings recorded"
              hint="Take an opening entry when the hall is handed over."
            />
          ) : null}

          {readings.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Reading</TableCell>
                  <TableCell>Hall / meter</TableCell>
                  <TableCell>Order</TableCell>
                  <TableCell align="right">Opening</TableCell>
                  <TableCell align="right">Closing</TableCell>
                  <TableCell align="right">Units</TableCell>
                  <TableCell align="right">Rate</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {readings.map((reading) => (
                  <TableRow key={reading.id} hover>
                    <TableCell>{reading.readingNumber}</TableCell>
                    <TableCell>
                      {reading.hallName}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {reading.meterNumber ?? 'no meter number'}
                      </Typography>
                    </TableCell>
                    <TableCell>{reading.orderNumber ?? '-'}</TableCell>
                    <TableCell align="right">
                      {formatNumber(reading.openingReading)}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {formatDateTime(reading.openingAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {reading.closingReading == null ? '—' : formatNumber(reading.closingReading)}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {formatDateTime(reading.closingAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(reading.unitsConsumed)}
                      {/* The sum behind the number, so a figure that looks
                          wrong can be checked without opening anything. */}
                      {reading.closingReading != null && reading.openingReading != null ? (
                        <Typography variant="caption" display="block" color="text.secondary">
                          ({formatNumber(reading.closingReading)} −{' '}
                          {formatNumber(reading.openingReading)}) × {formatNumber(reading.multiplyingFactor)}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="right">{formatMoney(reading.ratePerUnit)}</TableCell>
                    <TableCell align="right">{formatMoney(reading.amount)}</TableCell>
                    <TableCell>
                      <StatusChip status={reading.status} />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {reading.status === 'OPEN' && canWrite('utility') ? (
                          <Button size="small" onClick={() => setClosing(reading)}>
                            Closing entry
                          </Button>
                        ) : null}
                        {/* A dial misread is ours to fix until the guest has
                            been charged for it; after that it is a credit
                            note, and the server says so - unless an admin
                            steps in to correct the record itself. */}
                        {(reading.status !== 'BILLED' || isAdmin) && can('utility.edit') ? (
                          <Tooltip
                            title={
                              reading.status === 'BILLED'
                                ? 'Already invoiced — correcting it fixes the record, not the invoice'
                                : 'Correct this reading'
                            }
                          >
                            <IconButton
                              size="small"
                              aria-label={`Edit ${reading.readingNumber}`}
                              onClick={() => setCorrecting(reading)}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </CardContent>
      </Card>

      <OpeningDialog open={openingOpen} onClose={() => setOpeningOpen(false)} />
      <ClosingDialog readings={closing ? [closing] : []} onClose={() => setClosing(null)} />
      <CorrectionDialog reading={correcting} onClose={() => setCorrecting(null)} />
    </>
  )
}
